import React, { useEffect, useReducer, useMemo, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { bindActionCreators, compose } from 'redux';
import PropTypes from 'prop-types';
import CapRow from '../CapRow';
import CapColumn from '../CapColumn';
import Divider from './Divider';
import ProductModules from './ProductModules';
import BottomSectionProductModules from './BottomSectionProductModules';
import SubModules from './SubModules';
import { injectIntl } from 'react-intl';
import { getBreadcrumbs } from './utils';

import './_index.scss';
import {
  sortModulesByDisplayOrder,
  removeCommonPath,
  validateDisabledConfig,
  ChevronLeftIcon,
  CollapsibleNavDiv,
  SwitcherContainerDiv,
  SwitcherDiv,
  ArrowedDiv,
  reducerActions,
  reducer,
  initialState,
  FormattedMessageWrapper,
  getDifferentDomainUrl,
} from './utils';
import {
  COLLAPSED,
  EXPANDED,
  CLICKED,
  UrlWithFlashRegex,
  REQUEST,
} from './constants';
import CapIcon from '../CapIcon';
import isEmpty from 'lodash/isEmpty';
import { makeSelectNavigationConfig } from './selector';
import * as action from './actions';
import { saveItem, loadItem } from './utils';
const chevronLeft = require('./assets/chevron-left.svg');
const chevronLeftBlue = require('./assets/chevron-left-blue.svg');

export const CapCollapsibleLeftNavigation = ({
  switcherClicked: switcherClickedProp,
  leftNavbarExpanded,
  setBreadCrumbs,
  history,
  publicPath,
  actions,
  getNavigationConfigApi,
  navigationConfigData: {
    navigationConfig = [],
    navigationConfigLoading = '',
  } = {},
  handleGTMDataPush,
  pushFullUrl,
  intl: { formatMessage },
}) => {
  const [currentState, dispatchAction] = useReducer(reducer, initialState);
  const L1ModulesWithoutL2Modules = ["/home/ui", "/org/index"];
  const [showSwitcherContainerDiv, setShowSwitcherContainerDiv] = useState(null);

  useEffect(() => {
    switcherClickedProp(currentState.switcherClicked);
    actions.getNavigationConfig({ getNavigationConfigApi });
  }, []);

  useEffect(
    () => {
      if (navigationConfig?.length) {
        const sortedNavConfig = sortModulesByDisplayOrder(navigationConfig);
        dispatchAction({
          type: reducerActions.sortedConfig,
          value: sortedNavConfig,
        });
        selectedCurrentModuleAndSubModule(sortedNavConfig);
      }
    },
    [navigationConfig],
  );

  useEffect(
    () => {
      currentState.sortedConfig.length &&
        selectedCurrentModuleAndSubModule(currentState.sortedConfig);
    },
    [window.location.href],
  );

  useEffect(
    () => {
      const breadcrumbs = getBreadcrumbs(currentState, formatMessage);
      setBreadCrumbs(breadcrumbs);
    },
    [currentState.selectedL2Module,currentState.selectedL1Module],
  );

  const pushDataToGTM = useCallback(
    (operation = 'click', data = {}) =>
      handleGTMDataPush('leftNavigation', { action: operation, ...data }),
    [handleGTMDataPush],
  );


  //checks if L2 panel is visible in inital render
  useEffect(() => {
    const showSwitcherContainerDiv = L1ModulesWithoutL2Modules?.includes(
      publicPath,)
    if (!showSwitcherContainerDiv) {
      const storedSwitcherClicked = loadItem("switcherClicked");
      if (storedSwitcherClicked === null) {
        saveItem("switcherClicked", currentState.switcherClicked);
        dispatchAction({
          type: reducerActions.switcherClicked,
          value: currentState.switcherClicked,
        });
        pushDataToGTM(reducerActions.switcherClicked, {
          finalState: currentState.switcherClicked,
        });
        switcherClickedProp(currentState.switcherClicked);
      } else {
        dispatchAction({
          type: reducerActions.switcherClicked,
          value: storedSwitcherClicked,
        });
        pushDataToGTM(reducerActions.switcherClicked, {
          finalState: storedSwitcherClicked,
        });
        switcherClickedProp(storedSwitcherClicked);
      }
    }

  }, []);

  const toggleMenuCollapsed = useMemo(() => {
    return () => {
      const newValue = !currentState.switcherClicked;
      dispatchAction({
        type: reducerActions.switcherClicked,
        value: newValue,
      });
      pushDataToGTM(reducerActions.switcherClicked, {
        finalState: newValue,
      });
      switcherClickedProp(newValue);
      saveItem("switcherClicked", newValue);
    };
  }, [
    currentState.switcherClicked,
    dispatchAction,
    pushDataToGTM,
    switcherClickedProp,
  ]);

  const selectedCurrentModuleAndSubModule = config => {
    let prevSelectedL1Module;
    try {
      prevSelectedL1Module = JSON.parse(localStorage.getItem('prevSelectedL1Module'));
    } catch(error) {
      prevSelectedL1Module = null;
    }
    const currentWithFlashUrl = `${window.location.pathname}${window.location.search
      }`;
    const currentUrl = currentWithFlashUrl.replace(UrlWithFlashRegex, '');
    let result = null;
    const findSubmodule = (submodules, url, module) => {
      const actualUrl = url.endsWith('/') ? url.slice(0, url.length - 1) : url;
      !isEmpty(submodules) &&
        submodules?.forEach(submodule => {
          const { url = '' } = submodule || {};
          const actualSubmoduleUrl = url?.endsWith('/')
            ? url?.slice(0, url?.length - 1)
            : url;
          if (actualSubmoduleUrl === actualUrl) {
            result = { submodule, module };
          } else if (submodule?.subModules) {
            findSubmodule(submodule.subModules, actualUrl, module);
          }
        });
    };
    
    for (const module of config) {
      if (module.subModules) {
        findSubmodule(module.subModules, currentUrl, module);
        if (module.name === prevSelectedL1Module) {
          break;
        }
      } 
    }

    if (result) {
      dispatchAction({
        type: reducerActions.selectedL1Module,
        value: result?.module,
      });
      dispatchAction({
        type: reducerActions.selectedL2Module,
        value: result?.submodule,
      });
    } else {
      const onlyL1Module = config?.filter(
        module =>
          module?.publicPath?.replace(/\/$/, '') ===
          publicPath?.replace(/\/$/, ''),
      );
      dispatchAction({
        type: reducerActions.selectedL1Module,
        value: onlyL1Module?.[0],
      });
    }
  };

  const redirectToLink = ({ redirectionType, targetLocation, url, redirectToDifferentDomain, domainPattern = '', patternMapper = {}, hoveredRedirectionModule }) => {
    if (hoveredRedirectionModule) {
      localStorage.setItem('prevSelectedL1Module', JSON.stringify(hoveredRedirectionModule));
    }
    let isCompleteUrl;
    let completeUrl;
    let isSameApplication;
    let historyRedirectionURL;
    if (redirectToDifferentDomain) {
      isCompleteUrl = true;
      completeUrl = getDifferentDomainUrl({ domainPattern, patternMapper });
      redirectionType = 'location';
    } else {
      isCompleteUrl = url.includes('http');
      completeUrl = isCompleteUrl ? url : `${window.location.origin}${url}`;
      isSameApplication = url.includes(publicPath);
      historyRedirectionURL = pushFullUrl
        ? url
        : removeCommonPath(publicPath, url);
    }
    if (redirectionType === 'location' || !isSameApplication) {
      if (targetLocation === '_self') window.location.href = completeUrl;
      else window.open(completeUrl, targetLocation);
    } else {
      history.push(historyRedirectionURL);
    }
  };

  const handleRedirectionAndModuleSelection = ({
    disabled,
    redirectionType,
    targetLocation,
    url,
    name,
    l1_itemHovered,
    _id,
    redirectToDifferentDomain = false,
    domainPattern = '',
    patternMapper = {},
  }) => {
    if (!disabled) {
      redirectToLink({
        redirectionType, targetLocation, url, redirectToDifferentDomain, domainPattern, patternMapper, hoveredRedirectionModule: l1_itemHovered
      });
      pushDataToGTM('clickL2Item', {
        url,
        targetLocation,
        redirectionType,
        name,
      });
      selectedCurrentModuleAndSubModule(currentState.sortedConfig);
    }
  };

  const selectedL1ModuleObj = useMemo(
    () => {
      return currentState.sortedConfig?.find(
        config => config.name === currentState.selectedL1Module?.name,
      );
    },
    [currentState.sortedConfig, currentState.selectedL1Module],
  );

  useEffect(() => {
    const showSwitcherContainerDiv = L1ModulesWithoutL2Modules?.includes(
      publicPath,)
    showSwitcherContainerDiv && toggleMenuCollapsed();
    setShowSwitcherContainerDiv(showSwitcherContainerDiv);
  }, [publicPath])

  return (
    <CollapsibleNavDiv
      id="cap-collapsible-navbar"
      className={`cap-collapsible-new-nav ${!currentState.switcherClicked ? COLLAPSED : EXPANDED
        } ${CLICKED}`}
      switcherClicked={currentState.switcherClicked}
    >
      {!showSwitcherContainerDiv && <SwitcherContainerDiv
        onClick={toggleMenuCollapsed}
        switcherClicked={currentState.switcherClicked}
      >
        <SwitcherDiv
          className="switcher"
          switcherClicked={currentState.switcherClicked}
          onMouseOver={() =>
            dispatchAction({
              type: reducerActions.switcherHovered,
              value: true,
            })
          }
        >
          <ChevronLeftIcon
            className="switcher-icon"
            src={currentState.switcherHovered ? chevronLeftBlue : chevronLeft}
            switcherClicked={currentState.switcherClicked}
          />
        </SwitcherDiv>
      </SwitcherContainerDiv>}
      <CapRow>
        <CapColumn
          span={!currentState.switcherClicked ? 24 : 6}
          className="l1-module-column"
        >
          <CapIcon type="capillary" className="capillary-icon" />
          <Divider />
          {navigationConfigLoading !== REQUEST && (
            <>
              <ProductModules
                currentState={currentState}
                dispatchAction={dispatchAction}
                handleRedirectionAndModuleSelection={
                  handleRedirectionAndModuleSelection
                }
                redirectToLink={redirectToLink}
                pushDataToGTM={pushDataToGTM}
                leftNavbarExpanded={leftNavbarExpanded}
              />
              <BottomSectionProductModules
                currentState={currentState}
                dispatchAction={dispatchAction}
                handleRedirectionAndModuleSelection={
                  handleRedirectionAndModuleSelection
                }
                redirectToLink={redirectToLink}
                pushDataToGTM={pushDataToGTM}
                leftNavbarExpanded={leftNavbarExpanded}
              />
            </>
          )}
        </CapColumn>
        <CapColumn
          span={18}
          className={`l2-module-column ${
            currentState.switcherClicked ? 'block' : 'none'
          }`}
        >
          {navigationConfigLoading !== REQUEST && (
            <SubModules
              selectedL1ModuleObj={selectedL1ModuleObj}
              dispatchAction={dispatchAction}
              handleRedirectionAndModuleSelection={
                handleRedirectionAndModuleSelection
              }
              currentState={currentState}
              redirectToLink={redirectToLink}
              pushDataToGTM={pushDataToGTM}
            />
          )}
        </CapColumn>
      </CapRow>
    </CollapsibleNavDiv>
  );
};

CapCollapsibleLeftNavigation.defaultProps = {
  switcherClicked: () => {},
  leftNavbarExpanded: false,
  setBreadCrumbs: () => {},
  history: {},
  publicPath: '',
  handleGTMDataPush: () => {},
  pushFullUrl: false,
};

CapCollapsibleLeftNavigation.propTypes = {
  switcherClicked: PropTypes.func,
  leftNavbarExpanded: PropTypes.bool,
  setBreadCrumbs: PropTypes.func,
  history: PropTypes.object,
  publicPath: PropTypes.string,
  handleGTMDataPush: PropTypes.func,
  pushFullUrl: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  navigationConfigData: makeSelectNavigationConfig(),
});

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(action, dispatch),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(withConnect)(injectIntl(CapCollapsibleLeftNavigation));
